import { useEffect, useState } from 'react';
import { useLoaderData, useLocation } from 'react-router';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { useTracker } from '@archipro-website/tracker';

import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import type {
    loader,
    NetworkProfessional,
} from '@modules/professional/page/loaders/overview-page-loader.server';
import { professionalTabBase } from '@modules/professional/util/professional-tab-base';
import { fireNavMenuEvent } from '@modules/professional/component/sub-route-navigation/track-helpers';
import { useRailTileClickReport } from '~/ui-rocco/hooks/tile-click-event-for-section';
import { useLogImpressionOnDemand } from '~/ui-rocco/hooks/use-log-impression-on-demand';
import {
    detailCta,
    TileCarousel,
    type DetailTile,
    SECTION_TITLE_TYPE,
} from '~/ui-rocco/pages/detail-shared';
import { PROFILE_TILE_TEXT } from './professional-tile-props';

interface ProfessionalNetworkSectionProps {
    sectionNumber?: number;
}

interface NetworkGroup {
    category: string;
    items: { pro: NetworkProfessional; index: number }[];
}

/**
 * §7 — (03) Who we work with. The numbered section label, the View-our-Network
 * CTA, then a two-column body: the active partner's portrait on the left and a
 * 2-column, category-grouped name list on the right. Hovering a name swaps the
 * portrait; the first partner is active by default. The names align to the
 * category header — the active arrow hangs in the left margin.
 *
 * Desktop carries no standfirst or category pills: the section label says what
 * the block is, and the names are the content. Mobile still leads with the
 * DiscoverHeader, which keeps both.
 */
export const ProfessionalNetworkSection = (
    _props: ProfessionalNetworkSectionProps = {},
) => {
    const { networkProfessionals, professionalOverview } =
        useLoaderData<typeof loader>();
    const { pathname } = useLocation();
    const { desktop } = useAppDisplayModeContext();
    const tracker = useTracker();
    const [activeIndex, setActiveIndex] = useState(0);

    // Warm the browser cache for every partner banner so the desktop hover-swaps
    // are instant. Mobile uses a lazy-loaded carousel, so skip the preload there.
    useEffect(() => {
        if (!networkProfessionals || !desktop) return;
        const preloaded = networkProfessionals.map((pro) => {
            const img = new Image();
            img.src = pro.model.image?.src ?? '';
            return img;
        });
        return () => {
            preloaded.forEach((img) => {
                img.src = '';
            });
        };
    }, [networkProfessionals, desktop]);

    if (!networkProfessionals || networkProfessionals.length === 0) return null;

    // The profile's own tab base, so these stay inside whichever tree is
    // mounted — professionalOverview.Link is always the public path.
    const partnersTo = `${professionalTabBase(
        professionalOverview.URLSegment,
        pathname,
    )}/partners`;

    // Mobile shows the partners as a standard tile carousel (matching the other
    // detail-page related rails) rather than the desktop hover-swap module.
    const networkTiles: DetailTile[] = networkProfessionals.map((pro) => ({
        // The professional's own id, not the position in the list: it is what
        // a Save pins, and an index would pin whichever practice happened to
        // sit there.
        id: pro.model.id,
        // Arms the hover overlay; without a pinType the shared media renders
        // none at all.
        pinType: 'ProfessionalPin',
        website: pro.model.website,
        libraryItemId: pro.model.id,
        // A partner is its own professional: the id lets the tile's impression
        // and click carry it, and the subtitle below keeps the name off.
        professional: { id: pro.model.id },
        link: pro.model.link,
        title: pro.model.title,
        subtitle: pro.model.category,
        images: [{ src: pro.model.image?.src ?? '', alt: pro.model.title }],
        logo: pro.model.logo?.image?.src,
        logoBackground: pro.model.logo?.background,
    }));

    // Serialising through the loader widens the tile's model off its
    // discriminant, the same gap the Products and Projects tabs cast across.
    const groups = (networkProfessionals as NetworkProfessional[]).reduce<
        NetworkGroup[]
    >((acc, pro, index) => {
        let group = acc.find((entry) => entry.category === pro.model.category);
        if (!group) {
            group = { category: pro.model.category, items: [] };
            acc.push(group);
        }
        group.items.push({ pro, index });
        return acc;
    }, []);

    // Two explicit columns rather than CSS multi-column. Multicol decides where
    // to break by balancing height, and with `break-inside-avoid` on a tall
    // group it can start the second column below the first — leaving the two
    // category headings out of line. Splitting the groups ourselves puts both
    // columns at the top of the row whatever the data does.
    const columnWeight = (group: NetworkGroup) => group.items.length + 1;
    const totalWeight = groups.reduce((sum, g) => sum + columnWeight(g), 0);
    const groupColumns = groups.reduce<[NetworkGroup[], NetworkGroup[]]>(
        (columns, group) => {
            const firstWeight = columns[0].reduce(
                (sum, g) => sum + columnWeight(g),
                0,
            );
            // Fill the first column to half the total, then the second: order
            // is meaningful (biggest categories first), so this keeps it.
            columns[firstWeight < totalWeight / 2 ? 0 : 1].push(group);
            return columns;
        },
        [[], []],
    );

    const active = networkProfessionals[activeIndex] ?? networkProfessionals[0];
    if (!active) return null;

    return (
        <section className="bg-ground md:px-[3rem]">
            <div className="mx-auto w-full max-w-[114rem]">
                {/* Mobile: the title alone, in the section head's type as
                    Featured work sets it, over the partner carousel, with
                    View our Network under the tiles. */}
                <div className="md:hidden">
                    <div className="px-6 pt-12">
                        <h2
                            className={cn(
                                'm-0 text-[#323232]',
                                SECTION_TITLE_TYPE,
                            )}
                        >
                            Who we work with
                        </h2>
                    </div>
                    <TileCarousel
                        hideHeader
                        hidePagination
                        eyebrow="Who we work with"
                        tiles={networkTiles}
                        section="ProfessionalNetwork"
                        tileTextClassName={PROFILE_TILE_TEXT}
                        theme="light"
                        viewportClassName="px-6"
                        contentClassName="max-md:gap-2"
                        // Same reason as the header above: the carousel's
                        // light theme paints white over the section's grey.
                        className="bg-transparent max-md:pt-8"
                    />
                    <div className="px-6 pt-8">
                        <RoccoLink
                            to={partnersTo}
                            onClick={() =>
                                fireNavMenuEvent(
                                    { label: 'Network' },
                                    window.location.href,
                                    tracker,
                                )
                            }
                            className={detailCta(
                                'filled-light',
                                'w-fit max-md:h-[2.75rem] max-md:rounded-[var(--radius-btn-m)] max-md:px-6 max-md:text-sm',
                            )}
                        >
                            View our Network
                        </RoccoLink>
                    </div>
                </div>

                {/* Desktop: the editorial hover-swap module.
                    The closing gap belongs to the section, not to whichever
                    column happens to be longer. It used to come from the last
                    name group's own bottom margin, which sits inside the list
                    column — so a profile whose lists ran past the portrait got
                    a gap and one whose portrait ran past the lists had the
                    image butting straight into the next section. */}
                <div className="max-md:hidden md:pb-[2.1rem]">
                    {/* The statement's own voice, as it is set under the
                        banner: this names the section rather than numbering
                        it, so it reads as a line of the page rather than an
                        entry in a contents list. */}
                    <h2 className="m-0 font-sans font-medium tracking-[-0.025em] text-black md:text-[3rem] md:leading-[1.05]">
                        Who we work with
                    </h2>

                    <div className="md:mt-[3.5rem] md:flex md:gap-[14.4rem]">
                        {/* Left column: the active partner's portrait, flush to
                            the section gutter so it lines up with the rule and
                            the label above it. */}
                        <div className="shrink-0 md:w-[44.4rem]">
                            <div className="bg-neutral-200 relative aspect-[696/877] w-full overflow-hidden rounded-tile max-md:mt-8">
                                <img
                                    key={active.model.image?.src}
                                    src={active.model.image?.src}
                                    alt={active.model.title}
                                    className="absolute inset-0 size-full object-cover"
                                />
                            </div>
                        </div>

                        {/* Right column: the grouped name lists. The
                            description and CTA moved up into the summary row. */}
                        <div className="min-w-0 flex-1 max-md:mt-10">
                            <div className="flex gap-[4.8rem] max-md:mt-10">
                                {groupColumns.map((column, columnIndex) => (
                                    <div
                                        key={columnIndex}
                                        className="min-w-0 flex-1"
                                    >
                                        {column.map((group) => (
                                            <div
                                                key={group.category}
                                                className="max-md:mb-8 md:mb-[2.1rem] md:last:mb-0"
                                            >
                                                <p className="m-0 font-sans tracking-[-0.03em] text-[#323232] uppercase max-md:text-xs md:text-[1.0625rem]">
                                                    {group.category}
                                                </p>
                                                <ul className="m-0 mt-[0.48rem] list-none p-0 max-md:mt-2">
                                                    {group.items.map(
                                                        ({ pro, index }) => {
                                                            const isActive =
                                                                index ===
                                                                activeIndex;
                                                            return (
                                                                <NetworkNameLink
                                                                    key={index}
                                                                    pro={pro}
                                                                    isActive={
                                                                        isActive
                                                                    }
                                                                    onActivate={() =>
                                                                        setActiveIndex(
                                                                            index,
                                                                        )
                                                                    }
                                                                />
                                                            );
                                                        },
                                                    )}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

interface NetworkNameLinkProps {
    pro: NetworkProfessional;
    isActive: boolean;
    onActivate: () => void;
}

/**
 * One partner in the desktop name list. A name is the partner's tile on this
 * layout, so it reports what the mobile carousel's tile does: the impression,
 * and on a click the network rail's event with the site-wide tile click. The
 * partner's picture only shows while its name is hovered or focused, so that
 * is when the impression is logged, not when the list scrolls into view.
 */
const NetworkNameLink = ({
    pro,
    isActive,
    onActivate,
}: NetworkNameLinkProps) => {
    const reportClick = useRailTileClickReport(
        'ProfessionalNetwork',
        'ProfessionalTile',
    );
    const logImpression = useLogImpressionOnDemand({
        type: 'LibraryItem_Directory',
        itemID: pro.model.id,
        professionalID: pro.model.id,
    });
    const activate = () => {
        onActivate();
        logImpression();
    };

    return (
        <li>
            <RoccoLink
                to={pro.model.link}
                onMouseEnter={activate}
                onFocus={activate}
                onClick={() =>
                    reportClick({
                        id: pro.model.id,
                        title: pro.model.title,
                        professionalId: pro.model.id,
                    })
                }
                className={cn(
                    'relative block font-sans leading-[1.03] tracking-[-0.03em] transition-colors max-md:py-1 max-md:text-base md:py-[0.34rem] md:text-[1.125rem]',
                    isActive
                        ? 'text-[#181818]'
                        : 'text-[#bbb] hover:text-[#181818]',
                )}
            >
                <svg
                    viewBox="0 0 8 10"
                    aria-hidden="true"
                    className={cn(
                        'absolute top-1/2 right-full size-[0.45em] -translate-y-1/2 fill-current transition-opacity max-md:mr-1 md:mr-[0.48rem]',
                        isActive ? 'opacity-100' : 'opacity-0',
                    )}
                >
                    <path d="M0 0l8 5-8 5z" />
                </svg>
                {pro.model.title}
            </RoccoLink>
        </li>
    );
};
